import {useEffect, useRef, useState} from 'react';
import Slider from '@moredeal/react-slick';
import {
  SanityBlockCaseStudyLift,
  SanityCaseStudyType,
} from '~/types/sanityTypes';
import {IconCarouselArrow, InternalLink} from '../index';
import urlBuilder from '@sanity/image-url';
import {useSanityClient} from '~/lib/utils';

export function BlockCaseStudyLift({
  blockContent,
}: {
  blockContent: SanityBlockCaseStudyLift;
}) {
  const slider = useRef<any>(false);
  const sliderDom = useRef<any>();
  const [state, setState] = useState({
    activeSlide: '01',
  });

  const sliderSettings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 7500,
    arrows: false,
    beforeChange: (current: number, next: number) => {
      const nextIndex = next + 1;
      const nextString = nextIndex < 10 ? '0' + nextIndex : '' + nextIndex;
      setState({...state, activeSlide: nextString});
    },
  };

  useEffect(() => {
    slider.current = sliderDom.current;
  }, []);

  const prevSlide = () => {
    if (slider.current) {
      slider.current.slickPrev();
    }
  };

  const nextSlide = () => {
    if (slider.current) {
      slider.current.slickNext();
    }
  };

  const totalSlides =
    blockContent.caseStudies.length < 10
      ? '0' + blockContent.caseStudies.length
      : blockContent.caseStudies.length;

  return (
    <>
      {blockContent.title && (
        <div className="container">
          <h2
            className={`text-4xl mt-12 -mb-[0.3rem] text-outline-black text-transparent font-clash uppercase lg:text-5xl lg:leading-[3.5rem]`}
          >
            {blockContent.title}
          </h2>
        </div>
      )}
      <div className="relative bg-bgDark text-white py-6">
        <div className="flex justify-between absolute w-full top-[180px] md:top-[280px] z-30">
          <div
            className="border border-white ml-3 md:ml-4 cursor-pointer hover:bg-white hover:text-black"
            onClick={prevSlide}
          >
            <IconCarouselArrow direction="left" className="m-2" />
          </div>
          <div
            className="border border-white mr-3 md:mr-4 cursor-pointer hover:bg-white hover:text-black"
            onClick={nextSlide}
          >
            <IconCarouselArrow direction="right" className="m-2" />
          </div>
        </div>
        <div className="container">
          <div className="relative">
            <Slider {...sliderSettings} ref={sliderDom}>
              {blockContent.caseStudies.map((caseStudy) => {
                return (
                  <BlockCaseStudyLiftRow
                    key={caseStudy._id}
                    caseStudy={caseStudy}
                  />
                );
              })}
            </Slider>
            <div className="absolute top-[360px] md:top-[450px] -translate-y-[50%] z-20">
              <span className="font-clash tracking-wider text-4xl md:text-5xl mr-4">
                {state.activeSlide}
              </span>
              <span className="font-clash tracking-wider text-4xl md:text-5xl text-outline-white text-transparent">
                {' '}
                / {totalSlides}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function BlockCaseStudyLiftRow({caseStudy}: {caseStudy: SanityCaseStudyType}) {
  return (
    <div>
      <div className="relative w-full h-[360px] md:h-[450px]">
        {caseStudy.hero.backgroundImage && (
          <img
            src={urlBuilder(useSanityClient)
              .image(caseStudy.hero.backgroundImage)
              .auto('format')
              .url()}
            alt={caseStudy.hero.backgroundImage.altText || ' '}
            loading="lazy"
            style={{
              objectFit: 'cover',
              width: '100%',
              height: '100%',
              position: 'absolute',
            }}
          />
        )}
      </div>
      <div className="pb-6 pt-10 md:pt-16 lg:pb-9 lg:flex lg:justify-between">
        <div className="">
          <h3 className="pb-3 md:pb-5 my-0">{caseStudy.hero.title}</h3>
          {caseStudy.hero.tags &&
            caseStudy.hero.tags.map((tag) => (
              <span
                key={tag}
                className="bg-white text-black text-[12px] py-[4px] px-[8px] uppercase mt-4 mr-4 inline-block"
              >
                {tag}
              </span>
            ))}
        </div>
        <div className="pt-4 lg:pt-0 lg:pl-4 lg:w-[60%]">
          <p>
            {caseStudy.intro}{' '}
            <InternalLink
              slug={caseStudy.slug}
              documentType="caseStudy"
              className="mt-6 underline"
            >
              Lue lisää
            </InternalLink>
          </p>
        </div>
      </div>
    </div>
  );
}
